import { useEffect, useId, useRef, useState } from 'react';
import { MapPin, GitBranch, Building2, HelpCircle } from 'lucide-react';

export type InheritanceSourceKind = 'self' | 'ancestor' | 'tenant_default' | 'none';

type Props = {
  sourceKind: InheritanceSourceKind;
  /** ancestor のとき、出所ノードの表示名 (章②〜⑥の階層継承対象設定画面すべてで必須、D-59) */
  sourceNodeLabel?: string | null;
};

/** 階層継承の値がどこから来ているかを示す共通バッジ (D-59 必須要件)。
 *  659店舗×6階層規模でも設定不能にならないよう、値の隣に常にこのバッジを置くこと。
 *  ⚠️ ノード名は長くなりうるため1行に短縮ラベルで収め (line-clamp-1)、全名は
 *  aria-describedby + クリックで開くポップオーバーで見せる (`title`属性は使わない、
 *  ui-guidelines.md §2.4 — スクリーンリーダーが読み上げないため)。
 *  設計: docs/design/v04-shift-labor-management-detail.md §2-8 */
export function InheritanceSourceBadge({ sourceKind, sourceNodeLabel }: Props) {
  const [open, setOpen] = useState(false);
  const descId = useId();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  let Icon = HelpCircle;
  let shortLabel = '未設定';
  let fullText = 'この設定はまだ解決されていません。';
  let cls = 'bg-surface-sunken text-text-sub border-surface-border';

  if (sourceKind === 'self') {
    Icon = MapPin;
    shortLabel = 'この店舗で上書き済み';
    fullText = 'このノードで個別に上書きされた設定値です。';
    cls = 'bg-misespark-navy/10 text-misespark-navy border-misespark-navy/20';
  } else if (sourceKind === 'ancestor') {
    Icon = GitBranch;
    const label = sourceNodeLabel?.trim() || '上位ノード';
    shortLabel = `${label}の既定値`;
    fullText = `上位ノード「${label}」で設定された既定値を継承しています。`;
    cls = 'bg-alert-bg text-alert-text border-alert-border';
  } else if (sourceKind === 'tenant_default') {
    Icon = Building2;
    shortLabel = 'テナント既定';
    fullText = 'どのノードでも上書きされていない、テナント全体の既定値です。';
    cls = 'bg-surface-sunken text-text-sub border-surface-border';
  } else {
    fullText = 'この設定にはまだ値がありません (プラットフォームの既定値が使われます)。';
  }

  return (
    <div ref={containerRef} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-describedby={descId}
        aria-expanded={open}
        className={`inline-flex items-center gap-1 max-w-[200px] min-w-[44px] px-2 py-1 min-h-[44px] rounded text-xs border ${cls}`}
      >
        <Icon size={12} className="shrink-0" aria-hidden />
        <span className="truncate line-clamp-1">{shortLabel}</span>
      </button>
      <span id={descId} className="sr-only">{fullText}</span>
      {open && (
        <div
          role="tooltip"
          className="absolute z-50 left-0 top-full mt-1 w-max max-w-[260px] rounded border border-surface-border bg-white shadow-lg px-3 py-2 text-xs text-text-body break-words"
        >
          {fullText}
        </div>
      )}
    </div>
  );
}
